import { supabase } from '../lib/supabase/client';

// ============================================================================
// INTERFACES
// ============================================================================

export interface TherapistValidationResult {
  found: boolean;
  therapist?: {
    id: string;
    rut: string;
    nombre: string;
    apellido: string;
    email: string;
    especialidad: string;
    titulo?: string;
    registroProfesional?: string;
    numeroLicencia?: string;
    telefono?: string;
    estado: 'activo' | 'inactivo';
    avatar?: string;
    rol: 'terapeuta' | 'director';
    meet_url?: string;
  };
  message?: string;
}

export interface InvestorProfile {
  id: string;
  rut: string;
  nombre: string;
  apellido: string;
  email: string;
  telefono?: string;
  porcentaje_participacion: number;
  tipo_inversionista: 'socio' | 'inversionista' | 'fundador';
  fecha_ingreso: string;
}

export interface AuthenticationResult {
  found: boolean;
  userType?: 'therapist' | 'investor';
  therapist?: TherapistValidationResult['therapist'];
  investor?: InvestorProfile;
  message?: string;
}

// ── Drill-down de las KPI cards del dashboard del terapeuta ───────────────────
// El detalle detrás de cada número del overview (DashboardOverview). Fetch lazy
// al hacer click en la card — la PII (nombres de pacientes) NO viaja en la carga
// inicial del portal, solo cuando el terapeuta abre el detalle.

export type DashboardMetricKey = 'consultations' | 'newPatients' | 'weekly' | 'revenue';

export interface DashboardDetailConsulta {
  id: string;
  fecha_hora: string;
  duracion: number;
  estado: string;
  tipo_consulta: string;
  modalidad: string;
  valor_final: number;
  estado_pago_consulta: string;
  paciente_nombre: string;
}

export interface DashboardDetailNewPatient {
  id: string;
  nombre: string;
  primera_consulta_fecha: string;
  tipo_consulta: string;
}

export interface DashboardMetricDetail {
  metric: DashboardMetricKey;
  rangeLabel: string;
  /** consultations · weekly · revenue */
  consultas: DashboardDetailConsulta[];
  /** newPatients */
  newPatients: DashboardDetailNewPatient[];
  /** revenue — realizado = estado 'completada'; proyectado = resto de estados activos */
  revenue: { realizado: number; proyectado: number; total: number };
}

// ============================================================================
// SERVICIO
// ============================================================================

// SEC-040: el login lee su fila COMPLETA (rut / registro_profesional /
// numero_licencia / auth_user_id) vía la RPC `get_my_therapist_profile`
// (SECURITY DEFINER), no un `.from('terapeutas').select(...)` directo — esas
// columnas ya no están en el grant SELECT de `authenticated` sobre
// `terapeutas` (allowlist, sec040b). La RPC resuelve la identidad server-side
// desde auth.uid()/jwt email, filtra `activo=true`, devuelve 1 fila.

const INVESTOR_SELECT = `
  id, rut, nombre, apellido, email, telefono,
  porcentaje_participacion, tipo_inversionista, fecha_ingreso, activo
`;

function mapTherapist(t: any): TherapistValidationResult['therapist'] {
  return {
    id: t.id,
    rut: t.rut,
    nombre: t.nombre,
    apellido: t.apellido,
    email: t.email,
    especialidad: Array.isArray(t.especialidades) ? t.especialidades.join(', ') : t.titulo,
    titulo: t.titulo,
    registroProfesional: t.registro_profesional,
    numeroLicencia: t.numero_licencia,
    telefono: t.telefono,
    estado: 'activo',
    avatar: t.foto_url || t.imagen_perfil,
    rol: t.rol ?? 'terapeuta',
    meet_url: t.meet_url ?? undefined,
  };
}

function mapInvestor(i: any): InvestorProfile {
  return {
    id: i.id,
    rut: i.rut,
    nombre: i.nombre,
    apellido: i.apellido,
    email: i.email,
    telefono: i.telefono,
    porcentaje_participacion: i.porcentaje_participacion,
    tipo_inversionista: i.tipo_inversionista,
    fecha_ingreso: i.fecha_ingreso,
  };
}

class TherapistValidationService {
  /**
   * Autentica con email + contraseña via Supabase Auth.
   * Verifica primero en terapeutas; si no, en inversionistas.
   * Devuelve userType: 'therapist' | 'investor'.
   */
  static async authenticateTherapist(email: string, password: string): Promise<AuthenticationResult> {
    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });

      if (authError || !authData.user) {
        return { found: false, message: 'Credenciales inválidas. Verificá tu email y contraseña.' };
      }

      // ── Intento 1: terapeuta ──────────────────────────────────────────────
      // SEC-040: RPC en vez de `.from('terapeutas')` directo (ver nota arriba).
      const { data: therapist } = await supabase
        .rpc('get_my_therapist_profile')
        .maybeSingle();

      if (therapist) {
        // auth_user_id lo mantiene el trigger `sync_auth_user_id_on_terapeuta` (BEFORE INSERT/UPDATE OF email).
        return { found: true, userType: 'therapist', therapist: mapTherapist(therapist) };
      }

      // ── Intento 2: inversionista ──────────────────────────────────────────
      const { data: investor } = await supabase
        .from('inversionistas')
        .select(INVESTOR_SELECT)
        .eq('email', authData.user.email!)
        .eq('activo', true)
        .maybeSingle();

      if (investor) {
        return { found: true, userType: 'investor', investor: mapInvestor(investor) };
      }

      // No encontrado en ninguna tabla
      await supabase.auth.signOut();
      return { found: false, message: 'Tu cuenta no tiene acceso al portal.' };
    } catch (error) {
      console.error('❌ Error en autenticación:', error);
      return { found: false, message: 'Error de conexión. Intentá nuevamente.' };
    }
  }

  /**
   * Restaura sesión activa de Supabase Auth al montar el portal.
   * Chequea terapeutas primero; si no, inversionistas.
   */
  static async getTherapistFromSession(): Promise<AuthenticationResult> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.email) return { found: false };

      // ── Intento 1: terapeuta ──────────────────────────────────────────────
      // SEC-040: RPC en vez de `.from('terapeutas')` directo (ver nota arriba).
      const { data: therapist } = await supabase
        .rpc('get_my_therapist_profile')
        .maybeSingle();

      if (therapist) {
        return { found: true, userType: 'therapist', therapist: mapTherapist(therapist) };
      }

      // ── Intento 2: inversionista ──────────────────────────────────────────
      const { data: investor } = await supabase
        .from('inversionistas')
        .select(INVESTOR_SELECT)
        .eq('email', session.user.email)
        .eq('activo', true)
        .maybeSingle();

      if (investor) {
        return { found: true, userType: 'investor', investor: mapInvestor(investor) };
      }

      await supabase.auth.signOut();
      return { found: false, message: 'Tu cuenta no tiene acceso al portal.' };
    } catch {
      return { found: false };
    }
  }

  static async authenticateWithGoogle(): Promise<void> {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/panel`,
      },
    });
  }

  static async getTherapistStats(therapistId: string): Promise<any> {
    const defaultStats = {
      thisMonth: { consultations: 0, revenue: 0, completed: 0, newPatients: 0 },
      totalPatients: 0,
      weeklyConsultations: 0,
      upcomingConsultations: [],
      nextConsultation: null,
    };

    try {
      const thisMonth = new Date();
      thisMonth.setDate(1);
      thisMonth.setHours(0, 0, 0, 0);

      const ALL_ACTIVE_STATES = ['agendada', 'programada', 'confirmada', 'completada'];

      const { data: thisMonthConsults } = await supabase
        .from('consultas')
        .select('id, valor_final, estado, paciente_id')
        .eq('terapeuta_id', therapistId)
        .gte('fecha_hora', thisMonth.toISOString())
        .in('estado', ALL_ACTIVE_STATES);

      const { data: patients } = await supabase
        .from('consultas')
        .select('paciente_id')
        .eq('terapeuta_id', therapistId)
        .in('estado', ALL_ACTIVE_STATES);

      const uniquePatients = new Set((patients ?? []).map((p: any) => p.paciente_id)).size;

      // Pacientes nuevos este mes = pacientes de este mes que NO tenían consultas antes del 1ro
      const { data: priorPatients } = await supabase
        .from('consultas')
        .select('paciente_id')
        .eq('terapeuta_id', therapistId)
        .lt('fecha_hora', thisMonth.toISOString())
        .in('estado', ALL_ACTIVE_STATES);

      const priorPatientIds = new Set((priorPatients ?? []).map((p: any) => p.paciente_id));
      const newPatientsThisMonth = new Set(
        (thisMonthConsults ?? [])
          .map((c: any) => c.paciente_id)
          .filter((id: string | null) => id && !priorPatientIds.has(id))
      ).size;

      const now = new Date();
      const { data: upcomingConsults } = await supabase
        .from('consultas')
        .select('id, fecha_hora, duracion, estado, paciente_id')
        .eq('terapeuta_id', therapistId)
        .gte('fecha_hora', now.toISOString())
        .in('estado', ['agendada', 'programada', 'confirmada'])
        .order('fecha_hora', { ascending: true })
        .limit(5);

      // Horas agendadas esta semana (lun-dom, misma convención que getWeekDays en TherapistCalendar.tsx)
      const weekDay = now.getDay();
      const weekStart = new Date(now);
      weekStart.setDate(now.getDate() - weekDay + (weekDay === 0 ? -6 : 1));
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);

      const { count: weeklyConsultations } = await supabase
        .from('consultas')
        .select('id', { count: 'exact', head: true })
        .eq('terapeuta_id', therapistId)
        .gte('fecha_hora', weekStart.toISOString())
        .lt('fecha_hora', weekEnd.toISOString())
        .in('estado', ALL_ACTIVE_STATES);

      return {
        thisMonth: {
          consultations: thisMonthConsults?.length ?? 0,
          revenue: (thisMonthConsults ?? []).reduce((sum: number, c: any) => sum + (c.valor_final || 0), 0),
          completed: (thisMonthConsults ?? []).filter((c: any) => c.estado === 'completada').length,
          newPatients: newPatientsThisMonth,
        },
        totalPatients: uniquePatients,
        weeklyConsultations: weeklyConsultations ?? 0,
        upcomingConsultations: upcomingConsults ?? [],
        nextConsultation: upcomingConsults?.[0] ?? null,
      };
    } catch (error: any) {
      console.error('❌ Error obteniendo estadísticas:', error);
      return defaultStats;
    }
  }

  /**
   * Detalle fila-a-fila detrás de una KPI card del dashboard del terapeuta.
   *
   * Rangos y filtros de estado replicados 1:1 desde getTherapistStats — el
   * conteo del modal TIENE que coincidir con el número de la card:
   *   - consultations / revenue → mes en curso, estados activos
   *   - weekly                  → semana lun–dom (misma convención que getWeekDays)
   *   - newPatients             → pacientes del mes sin consulta previa al día 1
   *
   * Lectura directa de `consultas` filtrada por `terapeuta_id` — la RLS ya
   * scopea la tabla al terapeuta autenticado, no hace falta RPC.
   */
  static async getDashboardMetricDetail(
    therapistId: string,
    metric: DashboardMetricKey,
  ): Promise<DashboardMetricDetail> {
    const empty: DashboardMetricDetail = {
      metric,
      rangeLabel: '',
      consultas: [],
      newPatients: [],
      revenue: { realizado: 0, proyectado: 0, total: 0 },
    };

    const ALL_ACTIVE_STATES = ['agendada', 'programada', 'confirmada', 'completada'];
    // left join a `pacientes` (no `!inner`): si un paciente_id no fuera visible
    // bajo RLS, `!inner` descartaría esa consulta en silencio y el modal
    // mostraría MENOS filas que el número de la card. Con left join la fila
    // sigue y `pacienteNombre()` cae al fallback 'Paciente'.
    const CONSULTA_SELECT =
      'id, fecha_hora, duracion, estado, tipo_consulta, modalidad, valor_final, estado_pago_consulta, paciente_id, pacientes(nombre, apellido)';

    const pacienteNombre = (row: any): string => {
      const p = Array.isArray(row.pacientes) ? row.pacientes[0] : row.pacientes;
      return p ? `${p.nombre} ${p.apellido}`.trim() : 'Paciente';
    };
    const toConsulta = (row: any): DashboardDetailConsulta => ({
      id: row.id,
      fecha_hora: row.fecha_hora,
      duracion: row.duracion,
      estado: row.estado,
      tipo_consulta: row.tipo_consulta,
      modalidad: row.modalidad,
      valor_final: row.valor_final || 0,
      estado_pago_consulta: row.estado_pago_consulta,
      paciente_nombre: pacienteNombre(row),
    });

    try {
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
      const rawMonth = now.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
      const monthLabel = rawMonth.charAt(0).toUpperCase() + rawMonth.slice(1);

      // ── Semana lun–dom (idéntico a getTherapistStats) ──
      if (metric === 'weekly') {
        const weekDay = now.getDay();
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - weekDay + (weekDay === 0 ? -6 : 1));
        weekStart.setHours(0, 0, 0, 0);
        // Rango de la query: idéntico a getTherapistStats (+7*24h) para que el
        // conteo del modal coincida con la card.
        const weekEnd = new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
        // Para la etiqueta usamos el lunes+6 en días de calendario — así el
        // texto dice "lun–dom" aunque haya cambio de hora (DST Chile en sept)
        // que corre el `weekEnd` de arriba una hora.
        const weekEndLabel = new Date(weekStart);
        weekEndLabel.setDate(weekStart.getDate() + 6);

        const { data } = await supabase
          .from('consultas')
          .select(CONSULTA_SELECT)
          .eq('terapeuta_id', therapistId)
          .gte('fecha_hora', weekStart.toISOString())
          .lt('fecha_hora', weekEnd.toISOString())
          .in('estado', ALL_ACTIVE_STATES)
          .order('fecha_hora', { ascending: true });

        const fmt = (d: Date) => d.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' });
        return {
          ...empty,
          rangeLabel: `${fmt(weekStart)} – ${fmt(weekEndLabel)}`,
          consultas: (data ?? []).map(toConsulta),
        };
      }

      // ── Mes en curso: base común de consultations / revenue / newPatients ──
      const { data: monthRows } = await supabase
        .from('consultas')
        .select(CONSULTA_SELECT)
        .eq('terapeuta_id', therapistId)
        .gte('fecha_hora', monthStart.toISOString())
        .in('estado', ALL_ACTIVE_STATES)
        .order('fecha_hora', { ascending: true });

      const consultas = (monthRows ?? []).map(toConsulta);

      if (metric === 'consultations') {
        return { ...empty, rangeLabel: monthLabel, consultas };
      }

      if (metric === 'revenue') {
        const realizado = consultas
          .filter(c => c.estado === 'completada')
          .reduce((s, c) => s + c.valor_final, 0);
        const proyectado = consultas
          .filter(c => c.estado !== 'completada')
          .reduce((s, c) => s + c.valor_final, 0);
        return {
          ...empty,
          rangeLabel: monthLabel,
          consultas,
          revenue: { realizado, proyectado, total: realizado + proyectado },
        };
      }

      // ── metric === 'newPatients' ──
      const { data: priorRows } = await supabase
        .from('consultas')
        .select('paciente_id')
        .eq('terapeuta_id', therapistId)
        .lt('fecha_hora', monthStart.toISOString())
        .in('estado', ALL_ACTIVE_STATES);

      const priorIds = new Set((priorRows ?? []).map((r: any) => r.paciente_id));

      // primera consulta del mes por paciente nuevo (monthRows ya viene ordenado asc)
      const seen = new Map<string, DashboardDetailNewPatient>();
      for (const row of monthRows ?? []) {
        const pid = (row as any).paciente_id;
        if (!pid || priorIds.has(pid) || seen.has(pid)) continue;
        seen.set(pid, {
          id: pid,
          nombre: pacienteNombre(row),
          primera_consulta_fecha: (row as any).fecha_hora,
          tipo_consulta: (row as any).tipo_consulta,
        });
      }

      return { ...empty, rangeLabel: monthLabel, newPatients: Array.from(seen.values()) };
    } catch (error) {
      console.error('❌ Error obteniendo detalle de métrica del dashboard:', (error as { code?: string })?.code);
      return empty;
    }
  }
}

export default TherapistValidationService;
